/**
 * Prices on the public site, worked out from the plans as the billing console
 * stores them.
 *
 * `subscription_plans.base_price` and `subscription_license_types.price_per_seat`
 * are monthly and ex GST: invoices go to Xero with `LineAmountTypes: 'Exclusive'`
 * (HHNodeServer `xeroService`). The public site shows them inc GST (Rob,
 * 2026-10-09), so every figure shown passes through `incGst`.
 */

import type { LicenseType, SubscriptionPlan } from '@/types/subscription';
import { toNumOr, type Numeric } from '@/utils/numeric';

export const GST_RATE = 0.1;

/** Ex-GST dollars to inc-GST dollars, rounded to the cent. */
export function incGst(exGst: Numeric | undefined): number {
  return Math.round(toNumOr(exGst) * (1 + GST_RATE) * 100) / 100;
}

/** "$54.90", or "$54" when the cents are zero. */
export function formatPrice(amount: number): string {
  const whole = Math.abs(amount - Math.round(amount)) < 0.005;
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: whole ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

/** Active seat tiers, in the plan's own order. */
export function activeTiers(plan: SubscriptionPlan): LicenseType[] {
  return (plan.license_types ?? [])
    .filter((t) => t.is_active !== false)
    .slice()
    .sort((a, b) => a.sort_order - b.sort_order);
}

/** Cheapest paid seat, inc GST, or null when every tier is free. */
export function cheapestPaidSeat(plan: SubscriptionPlan): number | null {
  const paid = activeTiers(plan)
    .map((t) => incGst(t.price_per_seat))
    .filter((p) => p > 0);
  return paid.length ? Math.min(...paid) : null;
}

export interface EstimateLine {
  plan: SubscriptionPlan;
  /** Seats wanted per license type id. */
  seats: Record<number, number>;
}

/**
 * Monthly cost inc GST for the chosen modules and seats: each module's base
 * price plus seats × each tier's seat price. GST is added per line before
 * summing, as each figure shown on the page already includes it.
 */
export function estimateMonthly(lines: EstimateLine[]): number {
  let total = 0;
  for (const { plan, seats } of lines) {
    total += incGst(plan.base_price);
    for (const tier of activeTiers(plan)) {
      const n = Math.max(0, Math.floor(seats[tier.id] ?? 0));
      total += n * incGst(tier.price_per_seat);
    }
  }
  return Math.round(total * 100) / 100;
}

/** Annual discount from the plan, when one is set. */
export function annualDiscount(plan: SubscriptionPlan): number {
  return toNumOr((plan as SubscriptionPlan & { annual_discount_percent?: Numeric }).annual_discount_percent);
}

/**
 * The short price a card shows: the monthly base inc GST when there is one,
 * else the cheapest paid seat, else "Free".
 */
export function planFromLabel(plan: SubscriptionPlan): string {
  const base = incGst(plan.base_price);
  if (base > 0) return `${formatPrice(base)}/month inc GST`;
  const seat = cheapestPaidSeat(plan);
  if (seat != null) return `From ${formatPrice(seat)} a seat/month inc GST`;
  return 'Free';
}
